// StoreManagement.jsx
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Store, Pencil, Trash2, Search, Plus, MapPin, Phone, Mail,
  User, Home, Hash, Globe, Flag, X, Building, Eye, EyeOff
} from "lucide-react";

const StoreManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "SUPER_ADMIN") {
      navigate("/unauthorized");
      return;
    }
    fetchStores();
  }, [user, navigate]);

  const fetchStores = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/stores", {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      setStores(res.data || []);
    } catch (err) {
      setError("Failed to fetch stores");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (store = null, view = false) => {
    setIsEdit(!!store);
    setSelectedStore(store);
    setViewMode(view);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStore(null);
    setViewMode(false);
    setError("");
    setSuccess("");
  };

  const handleSave = async (formData) => {
    try {
      const token = localStorage.getItem("token");
      if (isEdit && selectedStore) {
        const res = await api.put(`/stores/${selectedStore.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStores(stores.map((s) => (s.id === selectedStore.id ? res.data : s)));
        setSuccess("Store updated successfully!");
      } else {
        const res = await api.post("/stores", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStores([...stores, res.data]);
        setSuccess("Store created successfully!");
      }
      setTimeout(closeModal, 1500);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save store");
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/stores/${confirmDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(stores.filter((s) => s.id !== confirmDelete));
      setConfirmDelete(null);
      setSuccess("Store deleted successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete store");
    }
  };

  const filteredStores = stores.filter(
    (store) =>
      store.name.toLowerCase().includes(search.toLowerCase()) ||
      store.email.toLowerCase().includes(search.toLowerCase()) ||
      store.phoneNumber.includes(search) ||
      store.ownerName.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedStores = filteredStores.slice(page * size, (page + 1) * size);
  const totalPages = Math.ceil(filteredStores.length / size);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row justify-between items-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Building className="h-8 w-8 text-indigo-600" />
            Store Management
          </h1>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => openModal()}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg mt-4 md:mt-0"
          >
            <Plus className="h-5 w-5" />
            Add New Store
          </motion.button>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 mb-6"
        >
          <div className="relative">
            <Search className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search stores by name, email, phone, or owner..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-400 outline-none transition"
            />
          </div>
        </motion.div>

        {/* Stores List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : filteredStores.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl shadow-lg"
          >
            <Store className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {search ? "No stores match your search" : "No stores found. Add a store to get started."}
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {paginatedStores.map((store, index) => (
              <motion.div
                key={store.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{store.name}</h3>
                      <p className="text-gray-500 dark:text-gray-400">{store.email}</p>
                    </div>
                    <div className="flex gap-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => openModal(store, true)}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                      >
                        <Eye className="h-4 w-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => openModal(store, false)}
                        className="p-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition"
                      >
                        <Pencil className="h-4 w-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setConfirmDelete(store.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-gray-700 dark:text-gray-300">
                      <User className="h-4 w-4 mr-2" />
                      <span>{store.ownerName}</span>
                    </div>

                    <div className="flex items-center text-gray-700 dark:text-gray-300">
                      <Phone className="h-4 w-4 mr-2" />
                      <span>{store.phoneNumber}</span>
                    </div>

                    <div className="flex items-center text-gray-700 dark:text-gray-300">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>{store.city}, {store.state}</span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${store.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {store.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Pagination */}
        {filteredStores.length > size && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex justify-center mt-8 gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </motion.button>
            <span className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl shadow-md">
              Page {page + 1} of {totalPages}
            </span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
              className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </motion.button>
          </motion.div>
        )}

        {/* Add/Edit Store Modal */}
        <AddStoreModal
          isOpen={showModal}
          onClose={closeModal}
          onSave={handleSave}
          store={selectedStore}
          isEdit={isEdit}
          viewMode={viewMode}
          error={error}
          success={success}
        />

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {confirmDelete && (
            <motion.div
              className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-sm p-6 text-center"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
              >
                <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">
                  Confirm Deletion
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to delete this store? This action cannot be undone.
                </p>

                {error && (
                  <div className="mb-4 p-3 rounded-xl bg-red-100 text-red-700 font-medium">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="mb-4 p-3 rounded-xl bg-green-100 text-green-700 font-medium">
                    {success}
                  </div>
                )}

                <div className="flex justify-center gap-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setConfirmDelete(null)}
                    className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDelete}
                    className="px-4 py-2 rounded-xl bg-red-600 text-white shadow-sm hover:bg-red-700 transition"
                  >
                    Delete
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// AddStoreModal Component
const AddStoreModal = ({ isOpen, onClose, onSave, store, isEdit, viewMode, error, success }) => {
  const [formData, setFormData] = useState(
    store || {
      name: "",
      ownerName: "",
      email: "",
      phoneNumber: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      active: true,
    }
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (store) {
      setFormData(store);
    } else {
      setFormData({
        name: "",
        ownerName: "",
        email: "",
        phoneNumber: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
        active: true,
      });
    }
  }, [store]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 relative max-h-[90vh] overflow-y-auto"
      >
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          onClick={onClose}
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
          {viewMode ? "View Store" : isEdit ? "Edit Store" : "Add Store"}
        </h2>

        {/* Error/Success Messages */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-xl bg-red-100 text-red-700 font-medium"
          >
            {error}
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-xl bg-green-100 text-green-700 font-medium"
          >
            {success}
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { name: "name", placeholder: "Store Name", icon: <Building />, required: true },
            { name: "ownerName", placeholder: "Owner Name", icon: <User />, required: true },
            { name: "email", placeholder: "Email", type: "email", icon: <Mail />, required: true },
            { name: "phoneNumber", placeholder: "Phone Number", icon: <Phone />, required: true },
            { name: "addressLine1", placeholder: "Address Line 1", icon: <Home />, full: true, required: true },
            { name: "addressLine2", placeholder: "Address Line 2 (Optional)", icon: <Home />, full: true },
            { name: "city", placeholder: "City", icon: <MapPin />, required: true },
            { name: "state", placeholder: "State", icon: <Flag />, required: true },
            { name: "postalCode", placeholder: "Postal Code", icon: <Hash />, required: true },
            { name: "country", placeholder: "Country", icon: <Globe />, required: true },
          ].map((field) => (
            <div key={field.name} className={`relative ${field.full ? "md:col-span-2" : ""}`}>
              <input
                type={field.type || "text"}
                name={field.name}
                placeholder={field.placeholder}
                value={formData[field.name] || ""}
                onChange={handleChange}
                required={field.required}
                disabled={viewMode}
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 
                border border-gray-300 dark:border-gray-700 
                focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                outline-none transition disabled:opacity-70 disabled:cursor-not-allowed"
              />
              <span className="absolute left-3 top-3.5 text-gray-400">
                {field.icon}
              </span>
            </div>
          ))}

          {/* Active Checkbox */}
          <div className="flex items-center gap-2 md:col-span-2 mt-2">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              disabled={viewMode}
              className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed"
            />
            <label className="text-gray-700 dark:text-gray-300">Active</label>
          </div>

          {/* Action Buttons */}
          {!viewMode && (
            <div className="md:col-span-2 flex justify-end gap-3 mt-6">
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="px-5 py-2 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={loading}
                className="px-6 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 
              hover:from-indigo-700 hover:to-purple-700 
              text-white font-semibold shadow-lg disabled:opacity-50 transition"
              >
                {loading ? "Saving..." : isEdit ? "Update Store" : "Create Store"}
              </motion.button>
            </div>
          )}
        </form>
      </motion.div>
    </div>
  );
};

export default StoreManagement;